import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FilterKeys } from '@common/enums/query-params/filters';
import MainContentBlock from '../ui/MainContentBlock';
import { DownloadIcon } from '@common/utils/icons/icons';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectNotifications } from '../../store/general/general-slice-selectors';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { getReportDataSets } from '../../store/general/general-actions';
import { convertReportQueryToParameters } from '../../models/general/notifications/report-parameters';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import { resetNotificationRestState, resetReportData } from '../../store/general/general-slice';
import { NotificationTrigger } from '../../models/enums/notifications';
import DashboardSummaryReport from '../pdfs/DashboardSummaryReport';
import { convertToDateTime } from '@common/utils/date';
import Box from '@mui/material/Box';
import MissionSummaryReport from '../pdfs/MissionSummaryReport';
import GeneralLoaderWrapper from '@common/components/General/GeneralLoaderWrapper';

const ReportOverlay: React.FC = () => {
  const { t } = useTranslation();

  const [filterParams, setFilterParams] = useSearchParams();
  const queryReport = filterParams.get(FilterKeys.Report);

  const dispatch = useAppDispatch();

  const notificationsData = useAppSelector((state) => selectNotifications(state));

  const [isSliderOpen, setIsSliderOpen] = useState(queryReport != null);

  const convertedParameters = useMemo(
    () => convertReportQueryToParameters(queryReport),
    [queryReport],
  );

  useEffect(() => {
    setIsSliderOpen(queryReport != null);
  }, [queryReport]);

  const closeOverlayClickHandler = useCallback(() => {
    setFilterParams((prevState) => {
      prevState.delete(FilterKeys.Report);
      return prevState;
    });
    dispatch(
      resetNotificationRestState({
        getReportDataStatus: HttpFetchStatus.Idle,
        getReportDataError: null,
      }),
    );
    dispatch(resetReportData());
    setIsSliderOpen(false);
  }, [dispatch, setFilterParams]);

  useEffect(() => {
    if (convertedParameters != null) {
      dispatch(getReportDataSets(convertedParameters));
    }
  }, [convertedParameters, dispatch]);

  if (!isSliderOpen) return null;

  const reportContent = (
    <React.Fragment>
      <Box width="100%" overflow="hidden" position="relative" height="calc(100% - 64px)">
        {(convertedParameters == null ||
          notificationsData.rest.getReportDataStatus === HttpFetchStatus.Failed) && (
          <CustomAlert header={t('error-invalid-report-parameters')} severity="error" />
        )}
        <GeneralLoaderWrapper
          getStatus={notificationsData.rest.getReportDataStatus}
          dataLoaded={notificationsData.reportDataSets != null}
          failedToLoadMessage={t('error-downloading-report')}
          loadingMessage={t('loading-report')}
        >
          <React.Fragment>
            {convertedParameters != null &&
              notificationsData.reportDataSets != null &&
              (convertedParameters.trigger === NotificationTrigger.DASHBOARD_SUMMARY ? (
                <DashboardSummaryReport
                  reportDataSets={notificationsData.reportDataSets}
                  start={convertToDateTime(convertedParameters.startTime)}
                  end={convertToDateTime(convertedParameters.endTime)}
                />
              ) : convertedParameters.trigger === NotificationTrigger.MISSION_SUMMARY ? (
                <MissionSummaryReport
                  reportDataSets={notificationsData.reportDataSets}
                  start={convertToDateTime(convertedParameters.startTime)}
                  end={convertToDateTime(convertedParameters.endTime)}
                />
              ) : null)}
          </React.Fragment>
        </GeneralLoaderWrapper>
      </Box>
    </React.Fragment>
  );

  return (
    <React.Fragment>
      <MainContentBlock
        isOverlayContent
        onClose={closeOverlayClickHandler}
        pages={[
          {
            open: queryReport != null,
            content: reportContent,
            tooltip: t('button-download'),
            title:
              convertedParameters?.trigger != null
                ? t('notification-trigger-' + convertedParameters.trigger)
                : '',
            icon: <DownloadIcon />,
            onClick: () => {},
          },
        ]}
      />
    </React.Fragment>
  );
};

export default ReportOverlay;
